import React, { useState } from 'react';
import { workflows } from '../workflows/workflowData';

// Transform workflows into the evaluation view format
const getAgentsFromWorkflows = () => {
  return workflows.map(w => ({
    id: w.id,
    name: w.name,
    currentVersion: w.currentVersion,
    currentScore: w.evaluation.currentScore,
    trend: w.evaluation.trend,
    totalLifetimePushes: w.evaluation.totalLifetimePushes,
    versions: w.evaluation.versions
  }));
};

// Breadcrumb component
function Breadcrumb({ items, onNavigate }) {
  return (
    <div style={{
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      padding: '12px 20px',
      borderBottom: '1px solid #e5e7eb',
      background: '#fafafa',
      fontSize: '13px'
    }}>
      {items.map((item, index) => (
        <React.Fragment key={index}>
          {index > 0 && (
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="#9ca3af" style={{ width: '14px', height: '14px' }}>
              <path fillRule="evenodd" d="M8.22 5.22a.75.75 0 0 1 1.06 0l4.25 4.25a.75.75 0 0 1 0 1.06l-4.25 4.25a.75.75 0 0 1-1.06-1.06L11.94 10 8.22 6.28a.75.75 0 0 1 0-1.06Z" clipRule="evenodd" />
            </svg>
          )}
          <button
            onClick={() => onNavigate(index)}
            style={{
              background: 'none',
              border: 'none',
              cursor: index < items.length - 1 ? 'pointer' : 'default',
              padding: '4px 8px',
              borderRadius: '4px',
              color: index < items.length - 1 ? '#3b82f6' : '#111827',
              fontWeight: index === items.length - 1 ? 600 : 400,
              fontSize: '13px',
              transition: 'background 0.15s'
            }}
            onMouseEnter={e => {
              if (index < items.length - 1) e.target.style.background = '#e5e7eb';
            }}
            onMouseLeave={e => {
              e.target.style.background = 'none';
            }}
          >
            {item.label}
          </button>
        </React.Fragment>
      ))}
    </div>
  );
}

// Trend arrow component
function TrendArrow({ trend }) {
  if (trend === 'improving') {
    return (
      <span style={{ color: '#16a34a', display: 'flex', alignItems: 'center', gap: '2px' }}>
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" style={{ width: '16px', height: '16px' }}>
          <path fillRule="evenodd" d="M10 17a.75.75 0 0 1-.75-.75V5.612L5.29 9.77a.75.75 0 0 1-1.08-1.04l5.25-5.5a.75.75 0 0 1 1.08 0l5.25 5.5a.75.75 0 1 1-1.08 1.04l-3.96-4.158V16.25A.75.75 0 0 1 10 17Z" clipRule="evenodd" />
        </svg>
      </span>
    );
  } else if (trend === 'declining') {
    return (
      <span style={{ color: '#dc2626', display: 'flex', alignItems: 'center', gap: '2px' }}>
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" style={{ width: '16px', height: '16px' }}>
          <path fillRule="evenodd" d="M10 3a.75.75 0 0 1 .75.75v10.638l3.96-4.158a.75.75 0 1 1 1.08 1.04l-5.25 5.5a.75.75 0 0 1-1.08 0l-5.25-5.5a.75.75 0 1 1 1.08-1.04l3.96 4.158V3.75A.75.75 0 0 1 10 3Z" clipRule="evenodd" />
        </svg>
      </span>
    );
  }
  return (
    <span style={{ color: '#6b7280', display: 'flex', alignItems: 'center' }}>
      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" style={{ width: '16px', height: '16px' }}>
        <path fillRule="evenodd" d="M4 10a.75.75 0 0 1 .75-.75h10.5a.75.75 0 0 1 0 1.5H4.75A.75.75 0 0 1 4 10Z" clipRule="evenodd" />
      </svg>
    </span>
  );
}

// Score color helper
function getScoreColor(score) {
  if (score < 0.03) return '#16a34a';
  if (score < 0.06) return '#ca8a04';
  return '#dc2626';
}

// Delta display component
function VersionDelta({ currentScore, previousScore }) {
  if (previousScore === null) {
    return <span style={{ fontSize: '12px', color: '#9ca3af' }}>—</span>;
  }

  const delta = ((previousScore - currentScore) / previousScore) * 100;
  const isImprovement = delta > 0;

  return (
    <span style={{
      fontSize: '12px',
      color: isImprovement ? '#16a34a' : '#dc2626',
      display: 'flex',
      alignItems: 'center',
      gap: '2px'
    }}>
      {isImprovement ? '↓' : '↑'} {Math.abs(delta).toFixed(0)}% from prev
    </span>
  );
}

// Gate badge component
function GateBadge({ gate }) {
  const colors = {
    lint: { bg: '#dbeafe', color: '#2563eb' },
    test: { bg: '#dcfce7', color: '#16a34a' },
    burn: { bg: '#fee2e2', color: '#dc2626' }
  };
  const style = colors[gate] || { bg: '#f3f4f6', color: '#6b7280' };

  return (
    <span style={{
      fontSize: '10px',
      padding: '2px 6px',
      borderRadius: '4px',
      background: style.bg,
      color: style.color,
      fontWeight: 500,
      textTransform: 'capitalize'
    }}>
      {gate}
    </span>
  );
}

// Level 1 - Agent Row
function AgentRow({ agent, onClick }) {
  const [isHovered, setIsHovered] = useState(false);

  return (
    <div
      onClick={onClick}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      style={{
        display: 'flex',
        alignItems: 'center',
        padding: '16px 20px',
        borderBottom: '1px solid #f3f4f6',
        background: isHovered ? '#fafafa' : '#ffffff',
        cursor: 'pointer',
        transition: 'background 0.15s ease'
      }}
    >
      {/* Agent Icon */}
      <div style={{
        width: '40px',
        height: '40px',
        borderRadius: '10px',
        background: agent.currentScore < 0.05 ? 'rgba(34, 197, 94, 0.1)' : 'rgba(239, 68, 68, 0.1)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        marginRight: '16px',
        flexShrink: 0
      }}>
        <span style={{ fontSize: '20px' }}>🤖</span>
      </div>

      {/* Name & Current Version */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px'
        }}>
          <span style={{ fontWeight: 600, fontSize: '15px', color: '#111827' }}>
            {agent.name}
          </span>
          <span style={{
            fontSize: '11px',
            padding: '2px 8px',
            borderRadius: '4px',
            background: '#f3f4f6',
            color: '#6b7280',
            fontWeight: 500
          }}>
            {agent.currentVersion}
          </span>
        </div>
        <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '4px' }}>
          {agent.versions.length} version{agent.versions.length !== 1 ? 's' : ''} • {agent.totalLifetimePushes} lifetime pushes
        </div>
      </div>

      {/* Trend */}
      <div style={{ marginRight: '24px' }}>
        <TrendArrow trend={agent.trend} />
      </div>

      {/* Current Score */}
      <div style={{ minWidth: '80px', textAlign: 'right', marginRight: '20px' }}>
        <div style={{
          fontSize: '18px',
          fontWeight: 600,
          color: getScoreColor(agent.currentScore)
        }}>
          {agent.currentScore.toFixed(3)}
        </div>
        <div style={{ fontSize: '11px', color: '#9ca3af' }}>r/t</div>
      </div>

      {/* Chevron */}
      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="#9ca3af" style={{ width: '20px', height: '20px' }}>
        <path fillRule="evenodd" d="M8.22 5.22a.75.75 0 0 1 1.06 0l4.25 4.25a.75.75 0 0 1 0 1.06l-4.25 4.25a.75.75 0 0 1-1.06-1.06L11.94 10 8.22 6.28a.75.75 0 0 1 0-1.06Z" clipRule="evenodd" />
      </svg>
    </div>
  );
}

// Level 2 - Version Row
function VersionRow({ version, onClick }) {
  const [isHovered, setIsHovered] = useState(false);

  return (
    <div
      onClick={onClick}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      style={{
        display: 'flex',
        alignItems: 'center',
        padding: '14px 20px',
        borderBottom: '1px solid #f3f4f6',
        background: isHovered ? '#fafafa' : '#ffffff',
        cursor: 'pointer',
        transition: 'background 0.15s ease'
      }}
    >
      {/* Version Number */}
      <div style={{
        width: '48px',
        height: '32px',
        borderRadius: '6px',
        background: '#f3f4f6',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        marginRight: '14px',
        fontSize: '13px',
        fontWeight: 600,
        color: '#374151'
      }}>
        {version.number}
      </div>

      {/* Status Badge */}
      <span style={{
        fontSize: '10px',
        padding: '2px 8px',
        borderRadius: '4px',
        background: version.status === 'active' ? '#dbeafe' : '#f3f4f6',
        color: version.status === 'active' ? '#2563eb' : '#6b7280',
        fontWeight: 500,
        textTransform: 'capitalize',
        marginRight: '16px'
      }}>
        {version.status}
      </span>

      {/* Score */}
      <div style={{ minWidth: '70px', marginRight: '12px' }}>
        <span style={{
          fontSize: '16px',
          fontWeight: 600,
          color: getScoreColor(version.score)
        }}>
          {version.score.toFixed(3)}
        </span>
        <span style={{ fontSize: '11px', color: '#9ca3af', marginLeft: '4px' }}>r/t</span>
      </div>

      {/* Delta */}
      <div style={{ minWidth: '120px', marginRight: '20px' }}>
        <VersionDelta currentScore={version.score} previousScore={version.previousScore} />
      </div>

      {/* Push Stats */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '20px',
        marginLeft: 'auto'
      }}>
        <div style={{ textAlign: 'center', minWidth: '45px' }}>
          <div style={{ fontSize: '14px', fontWeight: 500, color: '#111827' }}>{version.pushes}</div>
          <div style={{ fontSize: '10px', color: '#9ca3af' }}>pushes</div>
        </div>
        <div style={{ textAlign: 'center', minWidth: '45px' }}>
          <div style={{ fontSize: '14px', fontWeight: 500, color: '#16a34a' }}>{version.cleanPushes}</div>
          <div style={{ fontSize: '10px', color: '#9ca3af' }}>clean</div>
        </div>
        <div style={{ textAlign: 'center', minWidth: '45px' }}>
          <div style={{ fontSize: '14px', fontWeight: 500, color: '#dc2626' }}>{version.revertedPushes}</div>
          <div style={{ fontSize: '10px', color: '#9ca3af' }}>reverted</div>
        </div>
      </div>

      {/* Chevron */}
      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="#9ca3af" style={{ width: '18px', height: '18px', marginLeft: '12px' }}>
        <path fillRule="evenodd" d="M8.22 5.22a.75.75 0 0 1 1.06 0l4.25 4.25a.75.75 0 0 1 0 1.06l-4.25 4.25a.75.75 0 0 1-1.06-1.06L11.94 10 8.22 6.28a.75.75 0 0 1 0-1.06Z" clipRule="evenodd" />
      </svg>
    </div>
  );
}

// Level 3 - Push Row
function PushRow({ push, onClick }) {
  const [isHovered, setIsHovered] = useState(false);

  return (
    <div
      onClick={onClick}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      style={{
        display: 'flex',
        alignItems: 'center',
        padding: '12px 20px',
        borderBottom: '1px solid #f3f4f6',
        background: isHovered ? '#fafafa' : '#fff',
        cursor: 'pointer'
      }}
    >
      {/* Push Number & Status */}
      <div style={{
        width: '28px',
        height: '28px',
        borderRadius: '6px',
        background: push.status === 'merged' ? '#dcfce7' : '#fee2e2',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        marginRight: '12px',
        flexShrink: 0
      }}>
        {push.status === 'merged' ? (
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="#16a34a" style={{ width: '14px', height: '14px' }}>
            <path fillRule="evenodd" d="M16.704 4.153a.75.75 0 0 1 .143 1.052l-8 10.5a.75.75 0 0 1-1.127.075l-4.5-4.5a.75.75 0 0 1 1.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 0 1 1.05-.143Z" clipRule="evenodd" />
          </svg>
        ) : (
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="#dc2626" style={{ width: '14px', height: '14px' }}>
            <path fillRule="evenodd" d="M7.793 2.232a.75.75 0 0 1-.025 1.06L3.622 7.25h10.003a5.375 5.375 0 0 1 0 10.75H10.75a.75.75 0 0 1 0-1.5h2.875a3.875 3.875 0 0 0 0-7.75H3.622l4.146 3.957a.75.75 0 0 1-1.036 1.085l-5.5-5.25a.75.75 0 0 1 0-1.085l5.5-5.25a.75.75 0 0 1 1.06.025Z" clipRule="evenodd" />
          </svg>
        )}
      </div>

      {/* Push Number */}
      <div style={{
        fontSize: '12px',
        fontWeight: 500,
        color: '#6b7280',
        marginRight: '12px',
        minWidth: '32px'
      }}>
        #{push.number}
      </div>

      {/* Message */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{
          fontSize: '13px',
          fontWeight: 500,
          color: '#111827',
          fontFamily: 'ui-monospace, SFMono-Regular, SF Mono, Menlo, monospace',
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis'
        }}>
          {push.message}
        </div>
        <div style={{ fontSize: '11px', color: '#9ca3af', marginTop: '2px' }}>
          {push.time}
        </div>
      </div>

      {/* Status badge */}
      <span style={{
        fontSize: '10px',
        padding: '2px 8px',
        borderRadius: '4px',
        background: push.status === 'merged' ? '#dcfce7' : '#fee2e2',
        color: push.status === 'merged' ? '#16a34a' : '#dc2626',
        fontWeight: 500,
        marginRight: '16px'
      }}>
        {push.status}
      </span>

      {/* Diff */}
      <div style={{
        fontSize: '12px',
        fontFamily: 'ui-monospace, SFMono-Regular, SF Mono, Menlo, monospace',
        marginRight: '16px',
        minWidth: '80px'
      }}>
        <span style={{ color: '#16a34a' }}>+{push.additions}</span>
        {' '}
        <span style={{ color: '#dc2626' }}>-{push.deletions}</span>
      </div>

      {/* Tokens */}
      <div style={{
        fontSize: '12px',
        color: '#6b7280',
        marginRight: '16px',
        minWidth: '70px',
        textAlign: 'right'
      }}>
        {(push.tokens / 1000).toFixed(1)}K tokens
      </div>

      {/* Gates Fired */}
      <div style={{ display: 'flex', gap: '4px', minWidth: '100px' }}>
        {push.gatesFired.map(gate => (
          <GateBadge key={gate} gate={gate} />
        ))}
      </div>
    </div>
  );
}

// Push Detail View
function PushDetail({ push, onBack }) {
  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
      {/* Header */}
      <div style={{
        padding: '20px',
        borderBottom: '1px solid #e5e7eb'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
          <div style={{
            width: '36px',
            height: '36px',
            borderRadius: '8px',
            background: push.status === 'merged' ? '#dcfce7' : '#fee2e2',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            {push.status === 'merged' ? (
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="#16a34a" style={{ width: '18px', height: '18px' }}>
                <path fillRule="evenodd" d="M16.704 4.153a.75.75 0 0 1 .143 1.052l-8 10.5a.75.75 0 0 1-1.127.075l-4.5-4.5a.75.75 0 0 1 1.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 0 1 1.05-.143Z" clipRule="evenodd" />
              </svg>
            ) : (
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="#dc2626" style={{ width: '18px', height: '18px' }}>
                <path fillRule="evenodd" d="M7.793 2.232a.75.75 0 0 1-.025 1.06L3.622 7.25h10.003a5.375 5.375 0 0 1 0 10.75H10.75a.75.75 0 0 1 0-1.5h2.875a3.875 3.875 0 0 0 0-7.75H3.622l4.146 3.957a.75.75 0 0 1-1.036 1.085l-5.5-5.25a.75.75 0 0 1 0-1.085l5.5-5.25a.75.75 0 0 1 1.06.025Z" clipRule="evenodd" />
              </svg>
            )}
          </div>
          <div>
            <h3 style={{ margin: 0, fontSize: '16px', fontWeight: 600, color: '#111827' }}>
              Push #{push.number}
            </h3>
            <p style={{ margin: '2px 0 0', fontSize: '12px', color: '#6b7280' }}>
              {push.time}
            </p>
          </div>
          <span style={{
            marginLeft: 'auto',
            fontSize: '11px',
            padding: '4px 10px',
            borderRadius: '6px',
            background: push.status === 'merged' ? '#dcfce7' : '#fee2e2',
            color: push.status === 'merged' ? '#16a34a' : '#dc2626',
            fontWeight: 500,
            textTransform: 'capitalize'
          }}>
            {push.status}
          </span>
        </div>

        {/* Commit message */}
        <div style={{
          padding: '12px 16px',
          background: '#f9fafb',
          borderRadius: '8px',
          fontFamily: 'ui-monospace, SFMono-Regular, SF Mono, Menlo, monospace',
          fontSize: '13px',
          color: '#111827'
        }}>
          {push.message}
        </div>
      </div>

      {/* Stats Grid */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(4, 1fr)',
        gap: '16px',
        padding: '20px',
        borderBottom: '1px solid #e5e7eb'
      }}>
        <div style={{
          padding: '16px',
          background: '#f9fafb',
          borderRadius: '8px'
        }}>
          <div style={{ fontSize: '11px', color: '#6b7280', marginBottom: '4px', textTransform: 'uppercase' }}>
            Lines Added
          </div>
          <div style={{ fontSize: '24px', fontWeight: 600, color: '#16a34a' }}>
            +{push.additions}
          </div>
        </div>
        <div style={{
          padding: '16px',
          background: '#f9fafb',
          borderRadius: '8px'
        }}>
          <div style={{ fontSize: '11px', color: '#6b7280', marginBottom: '4px', textTransform: 'uppercase' }}>
            Lines Removed
          </div>
          <div style={{ fontSize: '24px', fontWeight: 600, color: '#dc2626' }}>
            -{push.deletions}
          </div>
        </div>
        <div style={{
          padding: '16px',
          background: '#f9fafb',
          borderRadius: '8px'
        }}>
          <div style={{ fontSize: '11px', color: '#6b7280', marginBottom: '4px', textTransform: 'uppercase' }}>
            Tokens Used
          </div>
          <div style={{ fontSize: '24px', fontWeight: 600, color: '#111827' }}>
            {(push.tokens / 1000).toFixed(1)}K
          </div>
        </div>
        <div style={{
          padding: '16px',
          background: '#f9fafb',
          borderRadius: '8px'
        }}>
          <div style={{ fontSize: '11px', color: '#6b7280', marginBottom: '4px', textTransform: 'uppercase' }}>
            Gates Fired
          </div>
          <div style={{ display: 'flex', gap: '4px', marginTop: '8px' }}>
            {push.gatesFired.map(gate => (
              <GateBadge key={gate} gate={gate} />
            ))}
          </div>
        </div>
      </div>

      {/* Placeholder for diff view */}
      <div style={{ flex: 1, padding: '20px' }}>
        <h4 style={{ margin: '0 0 12px', fontSize: '14px', fontWeight: 600, color: '#111827' }}>
          Changes
        </h4>
        <div style={{
          padding: '40px',
          background: '#f9fafb',
          borderRadius: '8px',
          textAlign: 'center',
          color: '#6b7280',
          fontSize: '14px'
        }}>
          Diff view coming soon
        </div>
      </div>
    </div>
  );
}

function EvaluationList({ searchQuery }) {
  // Navigation state: [{ type: 'agents'|'versions'|'pushes'|'push-detail', data: ... }]
  const [navStack, setNavStack] = useState([{ type: 'agents', data: null }]);

  const currentLevel = navStack[navStack.length - 1];

  // Build breadcrumb items
  const breadcrumbItems = navStack.map((level, index) => {
    if (level.type === 'agents') {
      return { label: 'Evaluations' };
    } else if (level.type === 'versions') {
      return { label: level.data.name };
    } else if (level.type === 'pushes') {
      return { label: level.data.number };
    } else if (level.type === 'push-detail') {
      return { label: `push #${level.data.number}` };
    }
    return { label: '' };
  });

  const handleNavigate = (index) => {
    if (index < navStack.length - 1) {
      setNavStack(navStack.slice(0, index + 1));
    }
  };

  const navigateTo = (level) => {
    setNavStack([...navStack, level]);
  };

  // Get agents from shared workflow data
  const agents = getAgentsFromWorkflows();

  // Filter agents by search
  const filteredAgents = agents.filter(a =>
    a.name.toLowerCase().includes((searchQuery || '').toLowerCase())
  );

  // Render based on current level
  const renderContent = () => {
    if (currentLevel.type === 'agents') {
      // Level 1: Agents list
      return (
        <div style={{ flex: 1, overflowY: 'auto' }}>
          {/* Header */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            padding: '12px 20px',
            borderBottom: '1px solid #e5e7eb',
            background: '#fafafa',
            fontSize: '11px',
            fontWeight: 600,
            color: '#6b7280',
            textTransform: 'uppercase',
            letterSpacing: '0.05em'
          }}>
            <div style={{ flex: 1 }}>Agent</div>
            <div style={{ width: '40px', textAlign: 'center' }}>Trend</div>
            <div style={{ width: '100px', textAlign: 'right', marginRight: '40px' }}>Score</div>
          </div>

          {filteredAgents.map(agent => (
            <AgentRow
              key={agent.id}
              agent={agent}
              onClick={() => navigateTo({ type: 'versions', data: agent })}
            />
          ))}

          {filteredAgents.length === 0 && (
            <div style={{
              padding: '40px',
              textAlign: 'center',
              color: '#6b7280',
              fontSize: '14px'
            }}>
              No agents found
            </div>
          )}
        </div>
      );
    } else if (currentLevel.type === 'versions') {
      // Level 2: Versions list
      const agent = currentLevel.data;
      return (
        <div style={{ flex: 1, overflowY: 'auto' }}>
          {/* Header */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            padding: '12px 20px',
            borderBottom: '1px solid #e5e7eb',
            background: '#fafafa',
            fontSize: '11px',
            fontWeight: 600,
            color: '#6b7280',
            textTransform: 'uppercase',
            letterSpacing: '0.05em'
          }}>
            <div style={{ width: '62px' }}>Version</div>
            <div style={{ width: '70px' }}>Status</div>
            <div style={{ width: '82px' }}>Score</div>
            <div style={{ width: '120px' }}>vs Previous</div>
            <div style={{ marginLeft: 'auto', display: 'flex', gap: '20px' }}>
              <div style={{ minWidth: '45px', textAlign: 'center' }}>Pushes</div>
              <div style={{ minWidth: '45px', textAlign: 'center' }}>Clean</div>
              <div style={{ minWidth: '45px', textAlign: 'center' }}>Reverted</div>
            </div>
            <div style={{ width: '30px' }}></div>
          </div>

          {agent.versions.map(version => (
            <VersionRow
              key={version.id}
              version={version}
              onClick={() => navigateTo({ type: 'pushes', data: version })}
            />
          ))}
        </div>
      );
    } else if (currentLevel.type === 'pushes') {
      // Level 3: Pushes list
      const version = currentLevel.data;
      return (
        <div style={{ flex: 1, overflowY: 'auto' }}>
          {/* Header */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            padding: '12px 20px',
            borderBottom: '1px solid #e5e7eb',
            background: '#fafafa',
            fontSize: '11px',
            fontWeight: 600,
            color: '#6b7280',
            textTransform: 'uppercase',
            letterSpacing: '0.05em'
          }}>
            <div style={{ width: '40px' }}></div>
            <div style={{ width: '44px' }}>#</div>
            <div style={{ flex: 1 }}>Commit</div>
            <div style={{ width: '70px' }}>Status</div>
            <div style={{ width: '96px' }}>Diff</div>
            <div style={{ width: '86px', textAlign: 'right' }}>Tokens</div>
            <div style={{ width: '100px', textAlign: 'right' }}>Gates</div>
          </div>

          {version.pushHistory.length > 0 ? (
            version.pushHistory.map(push => (
              <PushRow
                key={push.id}
                push={push}
                onClick={() => navigateTo({ type: 'push-detail', data: push })}
              />
            ))
          ) : (
            <div style={{
              padding: '40px',
              textAlign: 'center',
              color: '#6b7280'
            }}>
              <p style={{ fontSize: '14px' }}>No push history available</p>
              <p style={{ fontSize: '12px', marginTop: '4px' }}>Pushes will appear here as the workflow runs</p>
            </div>
          )}
        </div>
      );
    } else if (currentLevel.type === 'push-detail') {
      // Push detail view
      return <PushDetail push={currentLevel.data} />;
    }
  };

  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflowY: 'auto' }}>
      {/* Breadcrumb */}
      {navStack.length > 1 && (
        <Breadcrumb items={breadcrumbItems} onNavigate={handleNavigate} />
      )}

      {/* Content */}
      {renderContent()}

      {/* Footer */}
      <div style={{
        padding: '12px 20px',
        borderTop: '1px solid #e5e7eb',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        background: '#fafafa',
        fontSize: '12px',
        color: '#6b7280'
      }}>
        <span>
          {currentLevel.type === 'agents' && `${filteredAgents.length} agent${filteredAgents.length !== 1 ? 's' : ''}`}
          {currentLevel.type === 'versions' && `${currentLevel.data.versions.length} version${currentLevel.data.versions.length !== 1 ? 's' : ''}`}
          {currentLevel.type === 'pushes' && `${currentLevel.data.pushHistory.length} push${currentLevel.data.pushHistory.length !== 1 ? 'es' : ''}`}
          {currentLevel.type === 'push-detail' && 'Push details'}
        </span>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <span>Lower r/t is better</span>
          <span style={{ color: '#d1d5db' }}>•</span>
          <span style={{ color: '#16a34a' }}>&lt;0.03</span>
          <span style={{ color: '#ca8a04' }}>0.03-0.06</span>
          <span style={{ color: '#dc2626' }}>&gt;0.06</span>
        </div>
      </div>
    </div>
  );
}

export default EvaluationList;
